import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addSetting, getAllSetting } from "../../redux/services/authSlice";
import { toast } from "react-toastify";
import { LuPlus, LuTrash2, LuCheck, LuPercent } from "react-icons/lu";

const MockTestDiscount = () => {
  const dispatch = useDispatch();
  const settingData = useSelector((state) => state?.auth?.settingData);

  const [tiers, setTiers] = useState([]);
  const [questionCount, setQuestionCount] = useState(10);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      await dispatch(getAllSetting());
    } catch (err) {
      console.error(err);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    if (settingData) {
      if (Array.isArray(settingData.mockTestDiscountTiers)) {
        // Map any existing format to { minScore, maxScore, discount }
        const formatted = settingData.mockTestDiscountTiers.map((t) => ({
          minScore: Number(t.minScore) || 0,
          maxScore: Number(t.maxScore) || 0,
          discount: Number(t.discount ?? t.scoreDiscount) || 0,
        }));
        setTiers(formatted);
      } else {
        setTiers([]);
      }
      if (settingData.mockTestQuestionCount !== undefined) {
        setQuestionCount(Number(settingData.mockTestQuestionCount) || 10);
      }
    }
  }, [settingData]);

  const handleScoreRangeChange = (index, field, value) => {
    const updated = [...tiers];
    updated[index] = {
      ...updated[index],
      [field]: Number(value) || 0,
    };
    setTiers(updated);
  };

  const handleDiscountChange = (index, value) => {
    const updated = [...tiers];
    updated[index] = {
      ...updated[index],
      discount: Number(value) || 0,
    };
    setTiers(updated);
  };

  const addNewTier = () => {
    const lastTier = tiers[tiers.length - 1];
    const nextMin = lastTier ? Number(lastTier.maxScore) + 1 : 0;
    const nextMax = nextMin;

    setTiers([
      ...tiers,
      {
        minScore: nextMin,
        maxScore: nextMax,
        discount: 1000,
      },
    ]);
  };

  const removeTier = (index) => {
    const updated = tiers.filter((_, i) => i !== index);
    setTiers(updated);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        mockTestDiscountTiers: tiers,
        mockTestQuestionCount: Number(questionCount) || 10,
      };

      const res = await dispatch(addSetting(payload));
      if (res?.payload?.status === 200 || res?.payload?.data) {
        toast.success("Mock test settings saved successfully!");
        fetchSettings();
      } else {
        toast.success("Settings updated successfully!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to save settings.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-border pb-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="p-2 rounded-xl bg-primary/10 text-primary">
              <LuPercent className="w-6 h-6" />
            </span>
            <h1 className="text-2xl font-bold text-foreground">Score-wise Discount Settings</h1>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Configure marks range and discount amount for students taking the mock test. If no tiers are added, score discount is disabled.
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={addNewTier}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-primary bg-primary/10 hover:bg-primary/20 rounded-xl transition cursor-pointer"
          >
            <LuPlus className="w-4 h-4" />
            Add Tier
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-primary hover:bg-primary/90 rounded-xl shadow-theme transition disabled:opacity-50 cursor-pointer"
          >
            <LuCheck className="w-4 h-4" />
            {isSaving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {/* General Mock Test Settings */}
      <div className="bg-card rounded-2xl border border-border p-5 shadow-sm">
        <div className="max-w-md">
          <label className="block text-sm font-bold text-neutral-800 mb-1.5">
            Total Questions in Free Mock Test
          </label>
          <p className="text-xs text-neutral-500 mb-2">
            Number of questions served to students on the mock test page (default: 10). Questions are randomized on every test refresh.
          </p>
          <input
            type="number"
            min="1"
            max="100"
            value={questionCount}
            onChange={(e) => setQuestionCount(e.target.value)}
            className="w-full sm:w-48 px-3.5 py-2 font-bold text-neutral-800 border border-border rounded-xl focus:border-primary focus:outline-none"
            placeholder="10"
          />
        </div>
      </div>

      {/* Tier Table */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        {tiers.length === 0 ? (
          <div className="p-12 text-center text-neutral-400 space-y-3">
            <p className="text-base font-medium">No score discount tiers configured.</p>
            <p className="text-xs text-neutral-500">
              Score-wise discount is currently turned off. Regular course pricing and discounts will apply.
            </p>
            <button
              type="button"
              onClick={addNewTier}
              className="inline-flex items-center gap-1 px-4 py-2 text-xs font-bold text-primary bg-primary/10 rounded-xl hover:bg-primary/20 transition cursor-pointer"
            >
              <LuPlus className="w-4 h-4" /> Add First Tier
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-neutral-50/70 border-b border-border text-xs font-bold text-neutral-600 uppercase tracking-wider">
                  <th className="py-3.5 px-6">Score Range</th>
                  <th className="py-3.5 px-6">Score Discount (₹)</th>
                  <th className="py-3.5 px-6 text-center w-28">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                {tiers.map((tier, idx) => (
                  <tr key={idx} className="hover:bg-neutral-50/50 transition">
                    {/* Score Range */}
                    <td className="py-3.5 px-6 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="0"
                          value={tier.minScore}
                          onChange={(e) => handleScoreRangeChange(idx, "minScore", e.target.value)}
                          className="w-20 px-3 py-1.5 text-center font-bold text-neutral-800 border border-border rounded-xl focus:border-primary focus:outline-none"
                        />
                        <span className="text-neutral-400 font-bold">to</span>
                        <input
                          type="number"
                          min="0"
                          value={tier.maxScore}
                          onChange={(e) => handleScoreRangeChange(idx, "maxScore", e.target.value)}
                          className="w-20 px-3 py-1.5 text-center font-bold text-neutral-800 border border-border rounded-xl focus:border-primary focus:outline-none"
                        />
                        <span className="text-xs text-neutral-400 font-medium">marks</span>
                      </div>
                    </td>

                    {/* Discount */}
                    <td className="py-3.5 px-6 whitespace-nowrap">
                      <div className="relative w-48">
                        <span className="absolute left-3 top-2 text-neutral-400 font-bold">₹</span>
                        <input
                          type="number"
                          step="100"
                          min="0"
                          value={tier.discount}
                          onChange={(e) => handleDiscountChange(idx, e.target.value)}
                          className="w-full pl-8 pr-3 py-1.5 font-bold text-emerald-700 bg-emerald-50/40 border border-emerald-200 rounded-xl focus:border-emerald-500 focus:outline-none"
                          placeholder="1000"
                        />
                      </div>
                    </td>

                    {/* Action */}
                    <td className="py-3.5 px-6 text-center whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => removeTier(idx)}
                        className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-xl transition cursor-pointer"
                        title="Delete tier"
                      >
                        <LuTrash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MockTestDiscount;
